import { publicConfig as envConfig } from 'config/public-config';
import type { IncomingMessage } from 'http';

import { User } from 'models';
import { JSONHelpers } from 'helpers/JSONHelpers';

const USER_KEY = Symbol.for('dcide.requestUser');

type RequestWithUserCache = IncomingMessage & {
    [key: symbol]: User | null | undefined;
};

const getRequestUser = async (req: IncomingMessage): Promise<User | null> => {
    const requestWithUserCache = req as RequestWithUserCache;

    if (USER_KEY in requestWithUserCache) {
        return requestWithUserCache[USER_KEY] ?? null;
    }

    const user = await fetchUserFromApi(req);
    requestWithUserCache[USER_KEY] = user;

    return user;
};

const fetchUserFromApi = async (req: IncomingMessage): Promise<User | null> => {
    try {
        const cookies = parseCookies(req.headers.cookie ?? '');
        const dcideJwt = cookies['dcide-jwt'];

        if (!dcideJwt) {
            return null;
        }

        const environmentLockAccess = cookies['environmentLockAccess'];

        const response = await fetch(`${envConfig.urls.api}/users/me`, {
            headers: {
                authorization: dcideJwt,
                ...(environmentLockAccess ? { environmentLockAccess } : {}),
            },
        });

        const body = await response.text();

        const user = JSONHelpers.parse<{ user?: User | null }>(body, {
            onError: (error) => console.error('Error parsing user response:', error, body),
        })?.user;

        if (user) {
            return user;
        }
    } catch (error) {
        console.error('Error fetching user data:', error);
    }

    return null;
};

const parseCookies = (cookieHeader: string): Record<string, string> =>
    Object.fromEntries(
        cookieHeader
            .split(';')
            .map((c) => c.trim().split('='))
            .filter(([key]) => key)
            .map(([key, ...rest]) => [key.trim(), rest.join('=').trim()]),
    );

export { getRequestUser };
